/**
 * Admin - Companies Management
 * Gestão de empresas clientes
 */
import { useState, useEffect } from 'react'
import {
  Building2,
  Search,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Users,
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Download,
  ExternalLink,
} from 'lucide-react'
import { adminDb } from '../lib/supabase'

interface Company {
  id: string
  name: string
  cnpj: string
  email: string
  phone: string
  plan: string
  status: 'active' | 'inactive' | 'trial' | 'suspended'
  employee_count: number
  mrr: number
  created_at: string
  trial_ends_at?: string
}

export default function Companies() {
  const [loading, setLoading] = useState(true)
  const [companies, setCompanies] = useState<Company[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [planFilter, setPlanFilter] = useState<string>('all')
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)

  useEffect(() => {
    loadCompanies()
  }, [])

  const loadCompanies = async () => {
    try {
      setLoading(true)
      const { data, error } = await adminDb.companies.getAll()

      if (error) throw error

      if (data && data.length > 0) {
        setCompanies(data.map((c: any) => ({
          id: c.id,
          name: c.name || 'Empresa',
          cnpj: c.cnpj || '00.000.000/0001-00',
          email: c.email || 'contato@empresa.com',
          phone: c.phone || '(00) 0000-0000',
          plan: c.plan || 'Starter',
          status: c.status || 'active',
          employee_count: c.employee_count || 0,
          mrr: c.mrr || 0,
          created_at: c.created_at,
          trial_ends_at: c.trial_ends_at,
        })))
      } else {
        // Demo data
        setCompanies([
          { id: '1', name: 'ContruBem S.A.', cnpj: '12.345.678/0001-90', email: 'contato@construbem.com.br', phone: '(11) 3456-7890', plan: 'Enterprise', status: 'active', employee_count: 450, mrr: 4500, created_at: '2024-01-15' },
          { id: '2', name: 'TeleNord Comunicações', cnpj: '23.456.789/0001-01', email: 'contato@telenord.com.br', phone: '(21) 2345-6789', plan: 'Business', status: 'active', employee_count: 280, mrr: 2800, created_at: '2024-02-20' },
          { id: '3', name: 'Hospital Santa Cruz', cnpj: '34.567.890/0001-12', email: 'admin@hsc.com.br', phone: '(31) 3456-7890', plan: 'Enterprise', status: 'active', employee_count: 520, mrr: 5200, created_at: '2024-03-10' },
          { id: '4', name: 'Indústria MetalMax', cnpj: '45.678.901/0001-23', email: 'contato@metalmax.ind.br', phone: '(41) 4567-8901', plan: 'Business', status: 'trial', employee_count: 180, mrr: 0, created_at: '2024-11-01', trial_ends_at: '2024-12-01' },
          { id: '5', name: 'Energia Solar Plus', cnpj: '56.789.012/0001-34', email: 'comercial@solarplus.com.br', phone: '(51) 5678-9012', plan: 'Starter', status: 'active', employee_count: 95, mrr: 950, created_at: '2024-06-15' },
          { id: '6', name: 'LogiTrans Transportes', cnpj: '67.890.123/0001-45', email: 'contato@logitrans.com.br', phone: '(61) 6789-0123', plan: 'Business', status: 'inactive', employee_count: 150, mrr: 0, created_at: '2024-04-20' },
        ])
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta empresa?')) return

    try {
      await adminDb.companies.delete(id)
      setCompanies(companies.filter(c => c.id !== id))
    } catch (error) {
      console.error('Erro ao excluir empresa:', error)
    }
  }

  const handleExport = () => {
    let csv = 'Nome,CNPJ,Email,Plano,Status,Colaboradores,MRR\n'
    filteredCompanies.forEach(c => {
      csv += `"${c.name}","${c.cnpj}","${c.email}","${c.plan}","${c.status}",${c.employee_count},${c.mrr}\n`
    })

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `empresas_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; icon: any }> = {
      active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: CheckCircle2 },
      inactive: { bg: 'bg-slate-500/10', text: 'text-slate-400', icon: XCircle },
      trial: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: Clock },
      suspended: { bg: 'bg-rose-500/10', text: 'text-rose-400', icon: XCircle },
    }
    const style = styles[status] || styles.inactive
    const Icon = style.icon
    const labels: Record<string, string> = {
      active: 'Ativo',
      inactive: 'Inativo',
      trial: 'Trial',
      suspended: 'Suspenso',
    }

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {labels[status] || status}
      </span>
    )
  }

  const getPlanBadge = (plan: string) => {
    const styles: Record<string, string> = {
      Starter: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
      Business: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      Enterprise: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
    }

    return (
      <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${styles[plan] || styles.Starter}`}>
        {plan}
      </span>
    )
  }

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.cnpj.includes(searchTerm) ||
                         company.email.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter
    const matchesPlan = planFilter === 'all' || company.plan === planFilter

    return matchesSearch && matchesStatus && matchesPlan
  })

  const stats = {
    total: companies.length,
    active: companies.filter(c => c.status === 'active').length,
    trial: companies.filter(c => c.status === 'trial').length,
    totalMRR: companies.reduce((acc, c) => acc + c.mrr, 0),
    totalEmployees: companies.reduce((acc, c) => acc + c.employee_count, 0),
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 text-violet-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-violet-400" />
            Empresas
          </h1>
          <p className="text-slate-400">Gerencie as empresas clientes da plataforma</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Exportar
          </button>
          <button
            className="px-4 py-2 bg-violet-600 hover:bg-violet-500 rounded-lg text-white transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nova Empresa
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <p className="text-2xl font-bold text-white">{stats.total}</p>
          <p className="text-xs text-slate-500">Total de Empresas</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <p className="text-2xl font-bold text-emerald-400">{stats.active}</p>
          <p className="text-xs text-slate-500">Ativas</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <p className="text-2xl font-bold text-amber-400">{stats.trial}</p>
          <p className="text-xs text-slate-500">Em Trial</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <p className="text-2xl font-bold text-white">R$ {stats.totalMRR.toLocaleString('pt-BR')}</p>
          <p className="text-xs text-slate-500">MRR Total</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <p className="text-2xl font-bold text-white">{stats.totalEmployees.toLocaleString('pt-BR')}</p>
          <p className="text-xs text-slate-500">Colaboradores</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            type="text"
            placeholder="Buscar por nome, CNPJ ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
          />
        </div>

        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
          >
            <option value="all">Todos os Status</option>
            <option value="active">Ativo</option>
            <option value="inactive">Inativo</option>
            <option value="trial">Trial</option>
            <option value="suspended">Suspenso</option>
          </select>

          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
          >
            <option value="all">Todos os Planos</option>
            <option value="Starter">Starter</option>
            <option value="Business">Business</option>
            <option value="Enterprise">Enterprise</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="admin-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Plano
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Colaboradores
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  MRR
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Desde
                </th>
                <th className="text-right px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    Nenhuma empresa encontrada
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((company) => (
                  <tr key={company.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-white">{company.name}</p>
                        <p className="text-xs text-slate-500">{company.cnpj}</p>
                        <p className="text-xs text-slate-500">{company.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getPlanBadge(company.plan)}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(company.status)}
                      {company.status === 'trial' && company.trial_ends_at && (
                        <p className="text-xs text-slate-500 mt-1">
                          Expira em {new Date(company.trial_ends_at).toLocaleDateString('pt-BR')}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-white">{company.employee_count}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-emerald-400">
                        R$ {company.mrr.toLocaleString('pt-BR')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-400">
                          {new Date(company.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelectedCompany(company)}
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          title="Ver detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(company.id)}
                          className="p-2 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-700/50 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Mostrando {filteredCompanies.length} de {companies.length} empresas
          </p>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
              Anterior
            </button>
            <button className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded-lg">
              1
            </button>
            <button className="px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
              Próximo
            </button>
          </div>
        </div>
      </div>

      {/* Company Detail Modal */}
      {selectedCompany && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">{selectedCompany.name}</h2>
                <button
                  onClick={() => setSelectedCompany(null)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">CNPJ</p>
                  <p className="text-sm text-white">{selectedCompany.cnpj}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Email</p>
                  <p className="text-sm text-white">{selectedCompany.email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Telefone</p>
                  <p className="text-sm text-white">{selectedCompany.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Plano</p>
                  {getPlanBadge(selectedCompany.plan)}
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  {getStatusBadge(selectedCompany.status)}
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Cliente desde</p>
                  <p className="text-sm text-white">{new Date(selectedCompany.created_at).toLocaleDateString('pt-BR')}</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 text-center">
                  <p className="text-2xl font-bold text-white">{selectedCompany.employee_count}</p>
                  <p className="text-xs text-slate-500">Colaboradores</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 text-center">
                  <p className="text-2xl font-bold text-emerald-400">R$ {selectedCompany.mrr.toLocaleString('pt-BR')}</p>
                  <p className="text-xs text-slate-500">MRR</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 text-center">
                  <p className="text-2xl font-bold text-white">R$ {(selectedCompany.mrr * 12).toLocaleString('pt-BR')}</p>
                  <p className="text-xs text-slate-500">ARR</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button className="flex-1 px-4 py-2 bg-violet-600 hover:bg-violet-500 rounded-lg text-white transition-colors flex items-center justify-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Acessar Dashboard
                </button>
                <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors flex items-center gap-2">
                  <Edit2 className="w-4 h-4" />
                  Editar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
